import {
  Button,
  Icon,
  Logo,
  MobileBanner,
  NavbarGrid,
  ResponsiveImage,
  setMobileBannerHiddenCookie,
  SocialIcons,
  Typography,
  useBodyScrollLock,
} from "@sphtech/dmg-design-system";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import {
  closeIcon,
  hamburgerIcon,
  searchCircleBrownIcon,
  searchCircleIcon,
  searchIcon,
} from "../icons/navbarIcons";
import AnimatedLogo from "../icons/nuy50-2mb.gif";
import styles from "./navbarnuyou.module.css";

// import customStyles from "./custom.module.css";

const { NavLink } = Typography;

/**
 * There are 4 <sections> in NavbarNUYOU:
 * ISLAND - This contains the primary links, and the buttons to access MEGAMENU and SEARCHBAR
 * MEGAMENU - This slides in from the top when HAMBURGER ICON is clicked
 * SEARCHBAR - This fades in when the SEARCH ICON is clicked
 * BANNER - This fades in by default; hides when MEGAMENU or SEARCHBAR is active
 */
const NavbarNUYOU = ({
  navObject,
  sticky,
  currentLocation,
  contentHubProps,
  SubscribeBoxProps,
  socialIconProps,
  footerProps,
  bannerConfig,
}: React.ComponentProps<typeof NavbarGrid>) => {
  const { LogoSource, routes } = navObject;
  const { pathname } = currentLocation;

  const HIDE_ISLAND_THRESHOLD = 1500;
  const [isHideIsland, setIsHideIsland] = useState(false);
  const prevScroll = useRef(0);

  const [isMegamenu, setIsMegamenu] = useState(false);
  useBodyScrollLock(isMegamenu);

  const [isSearchbar, setIsSearchbar] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isBanner = !isHideIsland && !isMegamenu && !isSearchbar;

  const handleSearchSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      /**
       * 3 conditions to hide the island:
       * - User is scrolling downwards
       * - User scrolls past the threshold
       * - Megamenu is closed
       */
      const shouldHideIsland =
        currentScroll > prevScroll.current &&
        currentScroll > HIDE_ISLAND_THRESHOLD &&
        !isMegamenu;
      setIsHideIsland(shouldHideIsland);

      // Hides the searchbar if island is hidden
      if (shouldHideIsland) {
        setIsSearchbar(false);
      }

      prevScroll.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMegamenu]);

  return (
    <div className={cx(styles.container, isHideIsland && styles.hidden)}>
      {/* Overlay appears when megamenu is open */}
      <div
        className={cx(styles.overlay, isMegamenu && styles.open)}
        // Clicking anywhere outside will close megamenu
        onClick={() => {
          setIsMegamenu(false);
        }}
        role="presentation"
      ></div>

      {/* ISLAND SECTION */}
      <section className={cx(styles.island, sticky && styles.sticky)}>
        {/* ISLAND - Empty space for NUYOU logo */}
        <div
          className={cx(
            styles.logo_space,
            pathname === "/" && !sticky && styles.logo_giant,
          )}
        ></div>

        {/* ISLAND - primary items */}
        <div className={cx(styles.primary_items)} data-layout-position="navbar">
          {routes.primaryLinks.map((item, index) => {
            return (
              <NavLink
                key={index}
                href={item.url}
                // TODO: Custom styles for navlinks if needed
                // customClassName={customStyles["icon-nav-links-header"]}
                isActive={pathname === item.url}
              >
                {item.name}
              </NavLink>
            );
          })}
        </div>

        {/* ISLAND - utility button */}
        {!!routes.utilityLinks.length && (
          <div
            className={cx(styles.utility_items)}
            data-layout-position="navbar"
          >
            {routes.utilityLinks.map((item, index) => {
              if (item.as === "text") {
                return (
                  <NavLink key={index} href={item.url}>
                    {item.name}
                  </NavLink>
                );
              }
              return (
                <Button
                  key={index}
                  variant="primary"
                  href={item.url}
                  target={item.target}
                >
                  {item.name}
                </Button>
              );
            })}
          </div>
        )}

        {/* ISLAND - search icon */}
        <button
          className={styles.searchIcon}
          onClick={() => {
            setIsSearchbar(!isSearchbar);
            setIsMegamenu(false);
          }}
        >
          <Icon>
            <span
              className={cx(
                styles.search_circle_brown,
                isSearchbar && styles.open,
              )}
            >
              {searchCircleBrownIcon}
            </span>
            <span>{searchCircleIcon}</span>
          </Icon>
        </button>

        {/* ISLAND - hamburger icon */}
        <button
          onClick={() => {
            setIsMegamenu(!isMegamenu);
            setIsSearchbar(false);
          }}
          className={styles.hamburgerMenuBtnIcon}
          title="Menu"
          data-layout-position="menubutton"
        >
          <Icon>{isMegamenu ? closeIcon : hamburgerIcon}</Icon>
        </button>

        {/* ISLAND - NUYOU logo */}
        <button
          className={cx(
            styles.logo,
            pathname === "/" && !sticky && styles.logo_giant,
            isMegamenu && styles.isMegamenu,
            isSearchbar && styles.isSearchbar,
          )}
          onClick={() => {
            setIsMegamenu(false);
            setIsSearchbar(false);
          }}
        >
          <Logo
            href="/"
            src={pathname === "/" && !sticky ? AnimatedLogo : LogoSource}
            width="100%"
            height="100%"
            alt="logo"
          />
        </button>
      </section>

      {/* MEGAMENU SECTION */}
      <section
        className={cx(
          styles.island_under,
          styles.megamenu,
          !isMegamenu && styles.hidden,
          sticky && styles.sticky,
        )}
      >
        {/* MEGAMENU - category navlinks on MOBILE ONLY */}
        <div className={styles.category_box}>
          {routes.primaryLinks.map((item, index: number) => (
            <a
              href={item.url}
              target={item.target}
              className={styles.navlink}
              key={index}
              data-layout-position="megamenuitem"
            >
              {item.name}
            </a>
          ))}
          <hr />
        </div>

        {/* MEGAMENU - content hub */}
        {contentHubProps?.map((item) => {
          return (
            <a
              className={styles.navlink}
              href={item.path}
              key={item.path}
              data-layout-position="megamenuitem"
            >
              <p>{item.title}</p>
            </a>
          );
        })}

        {/* MEGAMENU - subscribe box */}
        {SubscribeBoxProps && (
          <div
            className={styles.subscribeBox}
            data-layout-position="megamenublock"
          >
            {SubscribeBoxProps.image && (
              <div className={styles.subscribeBoxImage}>
                <ResponsiveImage
                  {...SubscribeBoxProps.image}
                  displayWidth={98}
                  loading="lazy"
                />
              </div>
            )}

            <div>
              <p className={styles.subscribeBoxDesc}>
                {SubscribeBoxProps.description}
              </p>
              <Button
                href={SubscribeBoxProps.url}
                variant={SubscribeBoxProps.variant || "primary"}
                target="_blank"
              >
                {SubscribeBoxProps.buttonText}
              </Button>
            </div>
          </div>
        )}

        <hr />

        {/* MEGAMENU - social icons box */}
        {socialIconProps && (
          <div className={styles.socialIconsBox}>
            <p className={styles.socialIconDesc}>关注我们</p>
            <SocialIcons {...socialIconProps} />
          </div>
        )}

        <hr />

        {/* MEGAMENU - footer */}
        {footerProps?.map((item, index: number) => (
          <a
            href={item.url}
            target={item.target}
            className={styles.navlink}
            key={index}
          >
            {item.text}
          </a>
        ))}
      </section>

      {/* SEARCHBAR SECTION */}
      <section
        className={cx(
          styles.island_under,
          styles.fading_panel,
          styles.searchbar,
          isSearchbar && styles.open,
        )}
      >
        <Icon>{searchIcon}</Icon>

        <form className={styles.form} onSubmit={handleSearchSubmit}>
          <input
            className={styles.input}
            type="text"
            placeholder="搜寻文章"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
        </form>
      </section>

      {/* BANNER SECTION */}
      {bannerConfig && (
        <section
          className={cx(
            styles.island_under,
            styles.fading_panel,
            styles.banner,
            (sticky || pathname !== "/") && isBanner && styles.open,
          )}
        >
          <MobileBanner
            href={bannerConfig.url || ""}
            onClose={() => {
              setMobileBannerHiddenCookie(7, bannerConfig.cookieKey);
            }}
            dangerouslySetInnerHTML={{
              __html: bannerConfig.safeHtml || "",
            }}
            contentListOpts={{
              backgroundColor: "#FFD7DC",
              color: "black",
              showOnDesktop: "true",
            }}
            image={bannerConfig.image}
            imageWidth={bannerConfig.imageWidth}
            imageHeight={bannerConfig.imageHeight}
          />
        </section>
      )}
    </div>
  );
};

export default NavbarNUYOU;
